// @ts-nocheck
import { lazy, Suspense, useContext } from 'react'
import { Outlet } from 'react-router'
import { useQuery } from '@apollo/client/react'

import ErrorBoundary from 'js/components/ErrorBoundary'
import { SuspenseBoundary } from 'js/components/SuspenseBoundary'
import Header from 'js/layout/Header/Header'
import { START_OF_CONTENT_ELEMENT_ID } from 'js/lib/constants'
import { globalStateContext } from 'js/lib/GlobalState'
import { useSiteStore } from 'js/lib/stores'

import AdminOverlay from './AdminOverlay'
import CookieNotification from './CookieNotification'
import styles from './Layout.module.css'
import { GET_TOP_MENU_AND_FOOTER } from './Layout.queries'

// ChatOverlay imports the chat UI. Layout wraps every page, so a static import
// would ship that bundle on every route. Load it only when chat is enabled.
const ChatOverlay = lazy(() => import('./ChatOverlay/ChatOverlay'))

// PageRow imports every widget. Layout wraps every page, so a static import
// would ship that bundle on every route. Load it only when a footer is shown.
const PageRow = lazy(() => import('js/page/Page/PageRow'))

const Layout = () => {
    const cookieConsent = useSiteStore(({ site }) => site.cookieConsent)
    const { globalState, setGlobalState } = useContext(globalStateContext)

    const { data = {}, loading } = useQuery<any>(GET_TOP_MENU_AND_FOOTER)

    if (loading) {
        return null
    }

    const { site, viewer, siteSettings } = data
    const { chatEnabled, footerPageEnabled, footerRows } = site || {}

    const toggleEditMode = () => {
        setGlobalState((newState) => {
            newState.editMode = !globalState.editMode
        })
    }

    let cookieNotification
    if (cookieConsent && viewer && !viewer.loggedIn) {
        cookieNotification = <CookieNotification />
    }

    const hasFooter = footerPageEnabled && footerRows?.length > 0

    return (
        <div className={styles.wrapper}>
            <Header site={site} siteSettings={siteSettings} viewer={viewer} />
            <main
                className={styles.layoutMain}
                id={START_OF_CONTENT_ELEMENT_ID}
                tabIndex="-1"
            >
                <AdminOverlay
                    viewer={viewer}
                    editMode={globalState.editMode}
                    toggleEditMode={toggleEditMode}
                    positionLeft={!!globalState.showChatOverlay}
                />
                {chatEnabled && viewer?.loggedIn && (
                    // ChatOverlay is a Fixed overlay, and it renders nothing until opened.
                    // This is not a nice way to suppress the error, but it does get logged in Sentry.
                    // It's better than the page crashing.
                    // TODO: Implement SuspenseBoundary inside ChatOverlay
                    <ErrorBoundary fallback={<></>}>
                        <Suspense>
                            <ChatOverlay />
                        </Suspense>
                    </ErrorBoundary>
                )}
                {cookieNotification}
                <Outlet />
            </main>
            {hasFooter && (
                <footer>
                    {/* Prevent widgets in the footer from loading half the codebase for every route
                       TODO: make this better for SSR */}
                    <SuspenseBoundary>
                        {footerRows.map((row, idx) => (
                            <PageRow
                                key={`footer-rows-${idx}`}
                                guid={site.guid}
                                site={site}
                                rows={footerRows}
                                row={row}
                                rowIndex={idx}
                            />
                        ))}
                    </SuspenseBoundary>
                </footer>
            )}
        </div>
    )
}

export default Layout
